import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/ui/LanguageToggle';

export default function CookiePolicyPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary-600 p-2">
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt="KernLogic"
                  className="size-6 text-white"
                />
              </div>
              <span className="text-xl font-bold text-white">KernLogic</span>
            </Link>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/80 text-slate-300 hover:border-slate-500 hover:bg-slate-700/80 hover:text-white"
              >
                <Link to="/request-organization">
                  <ArrowLeft className="mr-2 size-4" />
                  {t('cookies.navigation.back')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-slate-700/50 bg-slate-800/90 p-8 shadow-xl backdrop-blur-sm">
            <h1 className="mb-4 text-3xl font-bold text-white">{t('cookies.title')}</h1>

            <p className="mb-8 text-sm text-slate-400">{t('cookies.effectiveDate')}</p>

            <div className="max-w-none space-y-6">
              <p className="text-slate-200">{t('cookies.introduction')}</p>

              {/* Section 1: What Are Cookies? */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.1.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.1.content')}</p>
              </div>

              {/* Section 2: Types of Cookies We Use */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.2.title')}
                </h2>

                <div className="space-y-4">
                  {/* 2.1 Necessary Cookies */}
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.2.1.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.2.1.purpose')}</p>
                    <p className="mt-2 text-slate-200">{t('cookies.sections.2.1.examples')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const examples = t('cookies.sections.2.1.examplesList', {
                          returnObjects: true,
                        });
                        const exampleArray = Array.isArray(examples) ? examples : [];
                        return exampleArray.map((example, index) => <li key={index}>{example}</li>);
                      })()}
                    </ul>
                  </div>

                  {/* 2.2 Analytics Cookies */}
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.2.2.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.2.2.purpose')}</p>
                    <p className="mt-2 text-slate-200">{t('cookies.sections.2.2.examples')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const examples = t('cookies.sections.2.2.examplesList', {
                          returnObjects: true,
                        });
                        const exampleArray = Array.isArray(examples) ? examples : [];
                        return exampleArray.map((example, index) => <li key={index}>{example}</li>);
                      })()}
                    </ul>
                  </div>

                  {/* 2.3 Functional Cookies */}
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.2.3.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.2.3.purpose')}</p>
                    <p className="mt-2 text-slate-200">{t('cookies.sections.2.3.examples')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const examples = t('cookies.sections.2.3.examplesList', {
                          returnObjects: true,
                        });
                        const exampleArray = Array.isArray(examples) ? examples : [];
                        return exampleArray.map((example, index) => <li key={index}>{example}</li>);
                      })()}
                    </ul>
                  </div>

                  {/* 2.4 Marketing Cookies */}
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.2.4.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.2.4.purpose')}</p>
                    <p className="mt-2 text-slate-200">{t('cookies.sections.2.4.examples')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const examples = t('cookies.sections.2.4.examplesList', {
                          returnObjects: true,
                        });
                        const exampleArray = Array.isArray(examples) ? examples : [];
                        return exampleArray.map((example, index) => <li key={index}>{example}</li>);
                      })()}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 3: Third-Party Cookies */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.3.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.3.content')}</p>
              </div>

              {/* Section 4: Consent Management */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.4.title')}
                </h2>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.4.1.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.4.1.content')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const options = t('cookies.sections.4.1.options', {
                          returnObjects: true,
                        });
                        const optionArray = Array.isArray(options) ? options : [];
                        return optionArray.map((option, index) => <li key={index}>{option}</li>);
                      })()}
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('cookies.sections.4.2.title')}</h3>
                    <p className="text-slate-200">{t('cookies.sections.4.2.content')}</p>
                  </div>
                </div>
              </div>

              {/* Section 5: How to Control Cookies */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.5.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.5.content')}</p>
                <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                  {(() => {
                    const browsers = t('cookies.sections.5.browsers', {
                      returnObjects: true,
                    });
                    const browserArray = Array.isArray(browsers) ? browsers : [];
                    return browserArray.map((browser, index) => <li key={index}>{browser}</li>);
                  })()}
                </ul>
              </div>

              {/* Section 6: Cookie Retention */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.6.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.6.content')}</p>
                <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                  {(() => {
                    const retention = t('cookies.sections.6.retention', {
                      returnObjects: true,
                    });
                    const retentionArray = Array.isArray(retention) ? retention : [];
                    return retentionArray.map((item, index) => <li key={index}>{item}</li>);
                  })()}
                </ul>
              </div>

              {/* Section 7: Updates to This Cookie Policy */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.7.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.7.content')}</p>
              </div>

              {/* Section 8: Contact Us */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">
                  {t('cookies.sections.8.title')}
                </h2>
                <p className="text-slate-200">{t('cookies.sections.8.content')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
